/**
 * 파일명 : evl0105m.tsx
 * 설명 : 인사평가화면 - 모바일
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18     지재용    최초작성
 * 1.1       2026-01-22     조동현    모바일 버전 생성
 ***********************************************/

/************************************************
 * Import 영역
 ************************************************/
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  message,
  Table,
  Select,
  Input,
  Button,
  Popconfirm,
  Card,
  Typography,
  Modal,
  Spin,
  Divider,
  Tabs,
  Space,
} from "antd";
import {
  CheckCircleFilled,
  SaveOutlined,
  SendOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";
import { isMobile } from "react-device-detect";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { Evl0105mAction } from "../../../redux/slice/evl/evl0105m_slice";
import type { IPropsEvl0105M } from "../../../redux/models/evl/evl0105m_model";

const { Title, Text } = Typography;
const { TextArea } = Input;

const gradeOptions = ["S", "A", "B", "C", "D"];
const gradeScoreMap: Record<string, number> = {
  S: 1,
  A: 0.9,
  B: 0.8,
  C: 0.7,
  D: 0.6,
};

// 업적/역량 테이블에서 공통으로 사용하는 Select 변경 핸들러
const handleSelectChange = (
  value: string,
  recordKey: string,
  field: string,
  setData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  setData((prev) =>
    prev.map((row) =>
      row.key === recordKey ? { ...row, [field]: value } : row,
    ),
  );
};

// 웹과 동일하게 결과를 소수점 2자리 문자열로 변환 (금융 계산 정확도 보장)
const calcTotal = (data: any[]) => {
  const sum = { self: 0, first: 0, second: 0, weight: 0 };
  data.forEach((row) => {
    const w = Number(row.weight) || 0;
    sum.weight += w;
    if (row.self) sum.self += (gradeScoreMap[row.self] || 0) * w;
    if (row.first) sum.first += (gradeScoreMap[row.first] || 0) * w;
    if (row.second) sum.second += (gradeScoreMap[row.second] || 0) * w;
  });
  // Web 버전과 동일하게 소수점 2자리로 변환
  return {
    weight: sum.weight,
    self: sum.self.toFixed(2),
    first: sum.first.toFixed(2),
    second: sum.second.toFixed(2),
  };
};

export default function Evl0105mMobile(props: IPropsEvl0105M) {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState<boolean>(true);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  const dispatch = useAppDispatch();
  const role = useAppSelector((s) => s.Evl0105m.role);

  // 핵심 상태
  const [rawSearchData, setRawSearchData] = useState<any>(null);
  const [userInfoTableData, setUserInfoTableData] = useState<any[]>([]);
  const [evaluatorTableData, setEvaluatorTableData] = useState<any[]>([]);
  // 직급(책임이상/선임이하) 판별 상태 및 ref
  const [isJunior, setIsJunior] = useState<boolean>(false);
  const isJuniorRef = useRef<boolean>(false);
  const [checkData, setcheckData] = useState<any>(null);                                // 조회정보 저장
const [forceDisabled, setForceDisabled] = useState(false);                            // 화면 전체 디스에이블시
  const [isHidden, setIsHidden] = useState(true);              // 저장, 제출, 제출취소 숨김(화면랜더할때 깜빡일수있어서 기본 true)                                               
  // 카운터 ref: 고유한 row key 생성용
  const rowCounterRef = useRef<number>(0);

  // 제출 여부 (제출되면 편집 금지)
  const isSubmitted = useMemo(() => {
    if (!rawSearchData || Object.keys(rawSearchData).length === 0) return false;
    if (role === "self") return rawSearchData?.slfSubYn === "Y";
    if (role === "first") return rawSearchData?.eval1SubYn === "Y";
    if (role === "second") return rawSearchData?.eval2SubYn === "Y";
    return false;
  }, [role, rawSearchData]);

  const [data, setData] = useState<any[]>([
    {
      key: `row-${++rowCounterRef.current}`,
      taskName: "",
      weight: "",
      objective: "",
      performance: "",
      effort: "",
    },
  ]);

  // 비중 합계 (data의 weight 필드 합계)
  const weightTotal = useMemo(
    () => data.reduce((sum, r) => sum + Number(r.weight || 0), 0),
    [data],
  );

  const [tableData, setTableData] = useState<any[]>([]); // 능력개발/업무개선 행
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [competencyData, setCompetencyData] = useState<any[]>([]);

  const jobGroupRef = useRef<HTMLDivElement | null>(null);
  const improvementSectionRef = useRef<HTMLDivElement | null>(null);
  const performanceSectionRef = useRef<HTMLDivElement | null>(null);
  const performanceEvalRef = useRef<HTMLDivElement | null>(null);

  // 업적/역량평가 상세설명 Modal
  const [descModalVisible, setDescModalVisible] = useState<boolean>(false);
  const [selectedItemDesc, setSelectedItemDesc] = useState<string>("");
  const [selectedItemName, setSelectedItemName] = useState<string>("");

  // tabs for 능력개발 및 업무 개선 사항
  const isTabEnabled = (tabKey: string) => {
    if (role === "self") return tabKey === "self";
    if (role === "first") return tabKey === "self" || tabKey === "first";
    if (role === "second") return true;
    return false;
  };

  const [activeTab, setActiveTab] = useState<string>(() =>
    isTabEnabled("self") ? "self" : isTabEnabled("first") ? "first" : "second",
  );
  // role이 바뀔 때마다 모든 탭에 대해 default를 강제 지정
  useEffect(() => {
    if (role === "self") setActiveTab("self");
    else if (role === "first") setActiveTab("first");
    else if (role === "second") setActiveTab("second");
    else {
      // fallback: 기존 로직 유지
      if (!isTabEnabled(activeTab)) {
        const firstEnabled = isTabEnabled("self")
          ? "self"
          : isTabEnabled("first")
            ? "first"
            : "second";
        setActiveTab(firstEnabled);
      }
    }
  }, [role]);

  // 표시할 탭 목록: role === 'self'이면 탭 숨김, 그렇지 않으면 활성 탭만 포함
  const visibleTabs = useMemo(() => {
    if (role === "self") return [] as string[];
    const tabs: string[] = [];
    if (isTabEnabled("self")) tabs.push("self");
    if (isTabEnabled("first")) tabs.push("first");
    if (isTabEnabled("second")) tabs.push("second");
    return tabs;
  }, [role]);

  const loginEmpNo = props.empNo || common.getEmpNo();
  const { hrMapId, evlYear, evlEnableYn, evlEditableYn } = props;

  // 편집 가능 여부 플래그(명확한 조건 사용으로 TS 경고 방지)
  const canEdit = {
    self: role === "self" && evlEnableYn !== "N" && !isSubmitted,
    first: role === "first" && evlEnableYn !== "N" && !isSubmitted,
    second: role === "second" && evlEnableYn !== "N" && !isSubmitted,
  };

  // 반응형 처리
  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // role 변경 시 로그 (초기 로드 포함)
  useEffect(() => {}, [role]);

  // 초기 props 검사 및 조회 호출
  useEffect(() => {
    if (!hrMapId || !loginEmpNo) {
      // loading을 true로 유지하여 background에 로딩 표시
      safeSet(() => setLoading(true));
      openConfirmModal({
        title: "인사평가 메인페이지에서 접근하십시오.",
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
        maskClosable: false, // 바깥 클릭 방지
        keyboard: false, // ESC 방지
        onOk: () => {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
        onCancel() {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
      });
      return;
    }
    loadEvalMap(hrMapId, loginEmpNo);
  }, [hrMapId, loginEmpNo]);

  const jobGroupMap: Record<string, string> = {
    "01": "비즈니스컨설턴트",
    "02": "전산전문직",
    "03": "일반직",
  };

useEffect(() => {
        // hidden 체크로직 실행
        if (!rawSearchData) return;

        const run = async () => {
          const result = await checkHidden();
        setIsHidden(result);
        };
        run();
      },[rawSearchData]);


  const loadEvalMap = (mapId: string, empNo: string) => {
    safeSet(() => setLoading(true));
    const requestData = { hrMapId: mapId, empNo, evlYear };
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",
      success: (evalMap: any) => {
        const item = evalMap.performanceItems?.[0];

        // 안전한 상태 업데이트
        safeSet(() => setRawSearchData(item));

        // 역할 확인 및 설정
        let myRole: "self" | "first" | "second" | "none" = "none";

        // HR 관리자 또는 2차평가 열람 권한은 2차 평가자(second) 권한 부여
        if (common.isHrAdmin() || props.canViewSecondEval === "Y") {
          myRole = "second";
        } else if (common.getEmpNo() === item?.empNo) myRole = "self";
        else if (common.getEmpNo() === item?.eval1EmpNo) myRole = "first";
        else if (common.getEmpNo() === item?.eval2EmpNo) myRole = "second";

        // 1차 평가자와 2차 평가자가 동일할 경우 권한 부여 처리
        if (
          !common.isHrAdmin() &&
          common.getEmpNo() === item?.empNo &&
          common.getEmpNo() === item?.eval1EmpNo &&
          common.getEmpNo() === item?.eval2EmpNo
        ) {
          if (item?.eval1SubYn === "N") {
            myRole = "first";
          } else if (item?.eval1SubYn === "Y") {
            myRole = "second";
          }
        }

        safeSet(() => dispatch(Evl0105mAction.setRole(myRole)));

        /* if (myRole !== "none") {
          messageApi.success(`${myRole} 권한으로 조회되었습니다.`);
        } else {
          messageApi.warning("로그인 사번이 매핑에 없습니다.");
        } 
        if (evlEnableYn === "N") {
          messageApi.warning("작성일자 만료로 인해 수정이 불가능합니다.");
        }*/

        // 본인 정보 설정
        safeSet(() =>
          setUserInfoTableData([
            {
              key: "user-personal",
              label1: "이름",
              value1: item?.empName || "",
              label2: "직급",
              value2: item?.empDutyNm || "",
              label3: "직위",
              value3: item?.evlPostNm || "",
            },
            {
              key: "user-dept",
              label1: "상위소속실",
              value1: item?.empUpperDeptNm || "",
              label2: "소속실",
              value2: item?.empDeptNm || "",
              label3: "직군",
              value3:
                jobGroupMap[String(item?.jobGrCd?.trim() || "none")] || "",
            },
          ]),
        );

        // 평가자 정보 설정
        safeSet(() =>
          setEvaluatorTableData([
            {
              key: "eval-1st",
              round: "1차",
              label1: "이름",
              value1: item?.eval1Name || "",
              label2: "직급",
              value2: item?.eval1DutyNm || "",
              label3: "직위",
              value3: item?.evl1PostNm || "",
            },
            {
              key: "eval-2nd",
              round: "2차",
              label1: "이름",
              value1: item?.eval2Name || "",
              label2: "직급",
              value2: item?.eval2DutyNm || "",
              label3: "직위",
              value3: item?.evl2PostNm || "",
            },
          ]),
        );

        // 직위에 따른 선임/책임 판별 (web과 동일하게)
        const postCd = item?.evlPostCd;
        const isJuniorLocal = postCd === "01" || postCd === "02";
        safeSet(() => setIsJunior(isJuniorLocal));
        isJuniorRef.current = isJuniorLocal;

        // Web 버전과 동일하게 선임이하=02, 책임이상=01 사용
        const prefix = isJuniorLocal ? "02" : "01";

        // tableData의 기본 행 설정 (prefix 기반 hrEvlItmId 사용)
        const defaultRows = [
          {
            key: "1",
            category: "IT직무능력",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}030601`,
          },
          {
            key: "2",
            category: "업무직무능력",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}030701`,
          },
          {
            key: "3",
            category: "자기계발",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}030801`,
          },
          {
            key: "4",
            category: "업무개선",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}030901`,
          },
          {
            key: "5",
            category: "부하직원육성",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}031001`,
          },
          {
            key: "6",
            category: "기타",
            content: "",
            outcome: "",
            opinion: "",
            opinion2: "",
            hrEvlItmId: `${prefix}031101`,
          },
        ];

        const res = evalMap.data || evalMap;
        if (
          Array.isArray(res.improvementItems) &&
          res.improvementItems.length > 0
        ) {
          const merged = defaultRows.map((r) => {
            const saved = res.improvementItems.find((it: any) => {
              // 우선 hrEvlItmId로 매칭, 없으면 itmNm으로 매칭 (trim 및 대소문자 무시)
              if (it.hrEvlItmId && it.hrEvlItmId === r.hrEvlItmId) return true;
              const itmName = (it.itmNm || it.itmName || it.itm_nm || "")
                .toString()
                .trim();
              return (
                itmName && itmName === (r.category || "").toString().trim()
              );
            });
            return saved
              ? {
                  ...r,
                  // 목표 및 결과 필드 매핑
                  content: saved.goalCntn ?? saved.goalCntn ?? "",
                  outcome: saved.resultCntn ?? saved.resultCntn ?? "",
                  opinion: saved.eval1Rmk || "",
                  opinion2: saved.eval2Rmk || "",
                }
              : r;
          });
          safeSet(() => setTableData(merged));
        } else safeSet(() => setTableData(defaultRows));

        // 평가 항목 및 등급 조회를 웹과 동일하게 분리하여 호출
        if (isMountedRef.current)
          loadPerformanceAndImprovement(hrMapId, props.hrEvlItmId);
      },
      error: (err: any) => {
        messageApi.error("평가자 매핑 조회에 실패했습니다.");
        safeSet(() => setLoading(false));
      },
    });
  };

  // 웹과 동일한 분리 조회: 수행업적/개선활동(TREVLR0019) 호출 후 등급(TREVLR0021) 조회
  const loadPerformanceAndImprovement = (
    hrMapIdParam: string,
    hrEvlItmIdParam: string = props.hrEvlItmId || "01010100",
  ) => {
    safeSet(() => setLoading(true));
    // Web 버전과 동일하게 prefix 기반으로 조회
    const prefix = isJuniorRef.current ? "02" : "01";
    commonProxy.callAsyncSvc(
      { hrMapId: hrMapIdParam, prefix },
      {
        txCd: "TREVLR0019",
        success: (res: any) => {
          const resData = res?.data ? res.data : res;
          // 응답에서 업적/역량 데이터 설정
          if (
            Array.isArray(resData.performanceItems) &&
            resData.performanceItems.length > 0
          ) {
            // 서버 데이터 로드 시 카운터 리셋
            rowCounterRef.current = 0;
            safeSet(() =>
              setData(
                resData.performanceItems.map((it: any) => ({
                  key: `row-${++rowCounterRef.current}`,
                  taskName: it.taskNm ?? "",
                  weight: it.weightRt != null ? String(it.weightRt) : "",
                  objective: it.goalCntn ?? "",
                  performance: it.resultCntn ?? "",
                  effort: it.processCntn ?? "",
                  hrEvlItmId: it.hrEvlItmId,
                })),
              ),
            );
          } else {
            // 기본 1행 제공(업적기술), 직급에 따라 hrEvlItmId 기본값 결정 (웹과 동일한 로직)
            const defaultId = isJuniorRef.current ? "02010201" : "01010201";
            // 기본값 로드 시 카운터 리셋
            rowCounterRef.current = 0;
            safeSet(() =>
              setData([
                {
                  key: `row-${++rowCounterRef.current}`,
                  taskName: "",
                  weight: "",
                  objective: "",
                  performance: "",
                  effort: "",
                  hrEvlItmId: defaultId,
                },
              ]),
            );
          }

          // 능력개발 및 업무개선 매핑 (웹과 동일)
          // Web 버전과 동일하게 isJunior 기반 prefix 정의 (TREVLR0019 응답의 improvementItems 매칭용)
          const perfPrefix = isJuniorRef.current ? "02" : "01";
          const defaultRows = [
            {
              key: "table-IT직무능력",
              category: "IT직무능력",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}030601`,
            },
            {
              key: "table-업무직무능력",
              category: "업무직무능력",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}030701`,
            },
            {
              key: "table-자기계발",
              category: "자기계발",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}030801`,
            },
            {
              key: "table-업무개선",
              category: "업무개선",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}030901`,
            },
            {
              key: "table-부하직원육성",
              category: "부하직원육성",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}031001`,
            },
            {
              key: "table-기타",
              category: "기타",
              content: "",
              outcome: "",
              opinion: "",
              opinion2: "",
              hrEvlItmId: `${perfPrefix}031101`,
            },
          ];

          if (
            Array.isArray(resData.improvementItems) &&
            resData.improvementItems.length > 0
          ) {
            const mergedRows = defaultRows.map((defaultRow) => {
              const savedData = resData.improvementItems.find((it: any) => {
                if (it.hrEvlItmId && it.hrEvlItmId === defaultRow.hrEvlItmId)
                  return true;
                const itmName = (it.itmNm || it.itmName || it.itm_nm || "")
                  .toString()
                  .trim();
                return (
                  itmName &&
                  itmName === (defaultRow.category || "").toString().trim()
                );
              });
              return savedData
                ? {
                    ...defaultRow,
                    content: savedData.goalCntn ?? "",
                    outcome: savedData.resultCntn ?? "",
                    opinion: savedData.eval1Rmk ?? "",
                    opinion2: savedData.eval2Rmk ?? "",
                  }
                : defaultRow;
            });
            safeSet(() => setTableData(mergedRows));
          } else {
            safeSet(() => setTableData(defaultRows));
          }

          // Web 버전과 동일하게 prefix 기반으로 TREVLR0021 조회
          const prefix = isJuniorRef.current ? "02" : "01";
          commonProxy.callAsyncSvc(
            { hrMapId: hrMapIdParam, prefix },
            {
              txCd: "TREVLR0021",
              success: (json: any) => {
                const gradeRes = json?.data ? json.data : json;
                if (!gradeRes) {
                  safeSet(() => setLoading(false));
                  return;
                }

                // Web 버전과 동일하게 list 필드에서 전체 데이터 추출
                const allData = Array.isArray(gradeRes?.list)
                  ? gradeRes.list
                  : [];

                if (Array.isArray(allData) && allData.length > 0) {
                  // 업적평가 (itmTypCd === '01')
                  const perfMapped = allData
                    .filter(
                      (it: any) =>
                        it.itmTypCd === "01" &&
                        it.hrEvlItmId !== "02010201" &&
                        it.hrEvlItmId !== "01010201",
                    )
                    .map((it: any) => ({
                      key: it.hrEvlItmId,
                      hrEvlItmId: it.hrEvlItmId,
                      dtlTypCdNm:
                        it.dtlTypCd === "01" ? "수행업적" : "수행업적",
                      item: it.itemNm || "",
                      description: it.itemDesc || "",
                      weight: it.weightRt || 0,
                      self: it.selfGrade || "",
                      first: it.eval1Grade || "",
                      second: it.eval2Grade || "",
                    }));

                  safeSet(() => setPerformanceData(perfMapped));

                  // 역량평가 (itmTypCd === '02')
                  const compMapped = allData
                    .filter(
                      (it: any) =>
                        it.itmTypCd === "02" &&
                        it.hrEvlItmId !== "02010201" &&
                        it.hrEvlItmId !== "01010201",
                    )
                    .map((it: any) => {
                      let dtlTypCdNm = "";
                      if (it.dtlTypCd === "03") dtlTypCdNm = "공통역량";
                      else if (it.dtlTypCd === "04") dtlTypCdNm = "역할역량";
                      else if (it.dtlTypCd === "05") dtlTypCdNm = "직무역량";
                      else dtlTypCdNm = "기타역량";

                      return {
                        key: it.hrEvlItmId,
                        hrEvlItmId: it.hrEvlItmId,
                        dtlTypCd: it.dtlTypCd,
                        dtlTypCdNm,
                        item: it.itemNm || "",
                        description: it.itemDesc || "",
                        weight: it.weightRt || 0,
                        self: it.selfGrade || "",
                        first: it.eval1Grade || "",
                        second: it.eval2Grade || "",
                      };
                    });

                  safeSet(() => setCompetencyData(compMapped));
                }

                /* messageApi.success("평가 정보를 불러왔습니다."); */
                safeSet(() => setLoading(false));
              },
              error: (e: any) => {
                messageApi.warning("등급 정보를 불러오는 데 실패했습니다.");
                safeSet(() => setLoading(false));
              },
            },
          );
        },
        error: (err: any) => {
          messageApi.error("평가 항목 조회에 실패했습니다.");
          safeSet(() => setLoading(false));
        },
      },
    );
  };

  // handlers
  const handleAddTask = () => {
    const defaultId = isJuniorRef.current ? "02010201" : "01010201";
    setData((prev) => [
      ...prev,
      {
        key: `row-${++rowCounterRef.current}`,
        taskName: "",
        weight: "",
        objective: "",
        performance: "",
        effort: "",
        hrEvlItmId: defaultId,
      },
    ]);
  };

  const deleteRow = (key: string) => {
    setData((prev) => prev.filter((row) => row.key !== key));
  };

  const handleUserInfoChange = (
    value: string,
    key: string,
    field: "value1" | "value2" | "value3",
  ) => {
    setUserInfoTableData((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    );
  };

  // TextArea 입력 성능 개선: 임시 로컬 값 보관 + 디바운스하여 부모 상태(setter) 업데이트 횟수 최소화
  const inputTimersRef = useRef<Record<string, number | null>>({});
  const shownExceedRef = useRef<Record<string, boolean>>({});
  const [tempInputs, setTempInputs] = useState<Record<string, string>>({});
  const confirmModalRefs = useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  const scrollAndMessage = (
    ref: React.RefObject<HTMLElement>,
    msg: string,
    type: "error" | "warning" = "error",
  ) => {
    messageApi[type](msg);
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
      }
    }, 300);
  };

  const scrollToSelector = (selector: string) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  // 컴포넌트 마운트 상태 확인용 ref (비동기 콜백에서 안전하게 setState를 호출하기 위함)
  const isMountedRef = useRef<boolean>(true);
  const safeSet = (fn: () => void) => {
    if (isMountedRef.current) fn();
  };
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  const getTempKey = (scope: string, key: string, field: string) =>
    `${scope}-${key}-${field}`;

  const getDisplayValue = (
    scope: string,
    key: string,
    field: string,
    baseValue: string,
  ) => tempInputs[getTempKey(scope, key, field)] ?? baseValue ?? "";

  const handleSimpleChange = (
    value: string,
    key: string,
    field: string,
    setter: React.Dispatch<React.SetStateAction<any[]>>,
    scope: string = "table",
  ) => {
    const tKey = getTempKey(scope, key, field);

    // 값 즉시 반영
    setTempInputs((prev) =>
      prev[tKey] === value ? prev : { ...prev, [tKey]: value },
    );

    // 디바운스하여 실제 배열 상태 업데이트 (300ms)
    const existing = inputTimersRef.current[tKey];
    if (existing) clearTimeout(existing as number);
    inputTimersRef.current[tKey] = window.setTimeout(() => {
      setter((prev) => {
        const idx = prev.findIndex((p) => p.key === key);
        if (idx === -1) return prev;
        const currentVal = prev[idx][field] || "";
        if (currentVal === value) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });

      // 임시값 정리
      setTempInputs((prev) => {
        const copy = { ...prev } as Record<string, string>;
        delete copy[tKey];
        return copy;
      });
      delete inputTimersRef.current[tKey];
    }, 300);
  };

  useEffect(() => {
    return () => {
      Object.values(inputTimersRef.current).forEach((t) => {
        if (t) clearTimeout(t);
      });
    };
  }, []);

  // save/submit
  const handleSave = () => {
    openConfirmModal({
      title: "저장하시겠습니까?",
      okText: "저장",
      cancelText: "취소",
      onOk: () => performSave(),
      onCancel: () => messageApi.info("저장 취소"),
    });
  };

  const performSave = async () => {
    if (!rawSearchData)
      return messageApi.error(
        "조회된 데이터가 없습니다. 먼저 조회를 완료해주세요.",
      );

    const { isBlocked, res } = await checkSubmitYn();
      // 제출여부 체크
       if (isBlocked) {
          common.openPage({ menuId: 'MEVL2025002' });
          return;
        }
        // 제출기한 체크
      if(!checkdate(res)){
          alert("평가 기간이 만료되었습니다.");
          common.openPage({ menuId: 'MEVL2025002' });
          return;
      }  
    if (role === "none") return messageApi.error("권한이 없습니다.");

    const jobGroupRow = userInfoTableData.find((r) => r.label3 === "직군");
    const selectedJobGrNm = jobGroupRow ? jobGroupRow.value3 : "";
    const selectedJobGrCd =
      Object.keys(jobGroupMap).find(
        (k) => jobGroupMap[k] === selectedJobGrNm,
      ) || "";

    const performanceItems = data.map((row, idx) => ({
      hrMapId,
      hrEvlItmId:
        row.hrEvlItmId || (isJuniorRef.current ? "02010201" : "01010201"),
      writeSeq: String(idx + 1).padStart(2, "0"),
      taskNm: row.taskName,
      goalCntn: row.objective,
      resultCntn: row.performance,
      processCntn: row.effort,
      weightRt: row.weight ? Number(row.weight) : null,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
    }));

    const improvementItems = tableData.map((row: any, idx: number) => ({
      hrMapId,
      hrEvlItmId:
        row.hrEvlItmId || (isJuniorRef.current ? "02010201" : "01010201"),
      writeSeq: String(idx + 1).padStart(2, "0"),
      goalCntn: row.content,
      resultCntn: row.outcome,
      eval1Rmk: row.opinion,
      eval2Rmk: row.opinion2,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
      jobGrCd: selectedJobGrCd,
    }));

    // Web 버전과 동일하게 TOTAL 행 제외하고 평가 점수 데이터 가공
    const performanceEvalItems = performanceData
      .filter((item) => item.hrEvlItmId !== "TOTAL")
      .map((item) => ({
        hrMapId,
        hrEvlItmId: item.hrEvlItmId,
        selfGrade: item.self || "",
        eval1Grade: item.first || "",
        eval2Grade: item.second || "",
      }));
    const competencyEvalItems = competencyData
      .filter((item) => item.hrEvlItmId !== "TOTAL")
      .map((item) => ({
        hrMapId,
        hrEvlItmId: item.hrEvlItmId,
        selfGrade: item.self || "",
        eval1Grade: item.first || "",
        eval2Grade: item.second || "",
      }));

    let saveFlags: any = {};
    if (role === "self") saveFlags = { slfSubYn: "N" };
    else if (role === "first") saveFlags = { eval1SubYn: "N" };
    else if (role === "second") saveFlags = { eval2SubYn: "N" };

    const payloadPrefix = isJuniorRef.current ? "02" : "01";
    const targetEmpNo = rawSearchData?.empNo;

    const payload = {
      ...rawSearchData,
      hrMapId,
      role,
      hrEvlItmId: props.hrEvlItmId,
      empNo: loginEmpNo,
      jobGrCd: selectedJobGrCd,
      performanceItems,
      performanceData,
      improvementItems,
      performanceEvalItems,
      competencyEvalItems,
      competencyData,
      tableData,
      ...saveFlags,
      slfEvlTotScr: grandTotal.self,
      eval1TotScr: grandTotal.first,
      eval2TotScr: grandTotal.second,
      fnlScr: grandTotal.avg,
      evlYear,
      prefix: payloadPrefix,
      targetEmpNo,
    };

    commonProxy.callAsyncSvc(payload, {
      txCd: "TREVLS0001",
      success: (res: any) => {
        messageApi.success("저장되었습니다.");
        loadEvalMap(hrMapId, loginEmpNo);
      },
      error: (err: any) => {
        messageApi.error("저장 중 오류가 발생했습니다.");
      },
    });
  };

  const handleSubmit = () => {
    openConfirmModal({
      title: "제출 확인",
      content: "제출하시겠습니까?\n제출완료 시 수정이 불가합니다.",
      okText: "제출",
      cancelText: "취소",
      onOk: () => {
        // 모달은 onOk 처리 후 자동으로 닫힙니다. performSubmit()로 검증/제출 로직 실행
        performSubmit();
      },
      onCancel: () => messageApi.info("제출 취소"),
    });
  };

  const performSubmit = async () => {
    if (!rawSearchData)
      return messageApi.error(
        "조회된 데이터가 없습니다. 먼저 조회를 완료해주세요.",
      );
    const { isBlocked, res } = await checkSubmitYn();
    // 제출여부 체크
      if (isBlocked) {
        common.openPage({ menuId: 'MEVL2025002' });
        return;
      }
      // 제출기한 체크
    if(!checkdate(res)){
        alert("평가 기간이 만료되었습니다.");
        common.openPage({ menuId: 'MEVL2025002' });
        return;
    }
    if (role === "none") return messageApi.error("권한이 없습니다.");

    const jobGroupRow = userInfoTableData.find((r) => r.label3 === "직군");
    const selectedJobGrNm = jobGroupRow ? jobGroupRow.value3 : "";
    const selectedJobGrCd =
      Object.keys(jobGroupMap).find(
        (k) => jobGroupMap[k] === selectedJobGrNm,
      ) || "";

    // 제출 시 직군 선택은 필수입니다
    if (!selectedJobGrCd) {
      scrollAndMessage(jobGroupRef, "직군을 선택해주세요.", "warning");
      return;
    }

    // 비중 합계 검증: 제출은 합계가 정확히 100%일 때만 허용
    if (weightTotal !== 100) {
      scrollAndMessage(
        performanceSectionRef,
        `비중 합계가 100%가 되어야 제출할 수 있습니다. 현재 합계: ${weightTotal}%`,
      );
      return;
    }

    // self 역할의 경우 모든 select/input/textarea 검증
    if (role === "self") {
      // 개선활동(테이블) 항목 검증
      for (let i = 0; i < tableData.length; i++) {
        const row = tableData[i];
        if (!row.content || !String(row.content).trim()) {
          messageApi.warning(
            `${row.category || "개선활동"} - 내용을 입력해주세요`,
          );
          scrollToSelector(`[data-improve-key="${row.key}"]`);
          return;
        }
        if (!row.outcome || !String(row.outcome).trim()) {
          messageApi.warning(
            `${row.category || "개선활동"} - 성과를 입력해주세요`,
          );
          scrollToSelector(`[data-improve-key="${row.key}"]`);
          return;
        }
      }

      // 성과업적(업무) 항목 검증
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        if (!row.taskName || !String(row.taskName).trim()) {
          messageApi.warning(`[업적기술] - 업무명을 입력해주세요 (${i + 1}행)`);
          scrollToSelector(`[data-task-index="${i}"]`);
          return;
        }
        if (
          row.weight === undefined ||
          row.weight === null ||
          String(row.weight).trim() === ""
        ) {
          messageApi.warning(`[업적기술] - 비중을 입력해주세요 (${i + 1}행)`);
          scrollToSelector(`[data-task-index="${i}"]`);
          return;
        }
        if (!row.objective || !String(row.objective).trim()) {
          messageApi.warning(
            `[업적기술] - 업무 목표를 입력해주세요 (${i + 1}행)`,
          );
          scrollToSelector(`[data-task-index="${i}"]`);
          return;
        }
        if (!row.performance || !String(row.performance).trim()) {
          messageApi.warning(
            `[업적기술] - 업무 성과를 입력해주세요 (${i + 1}행)`,
          );
          scrollToSelector(`[data-task-index="${i}"]`);
          return;
        }
        if (!row.effort || !String(row.effort).trim()) {
          messageApi.warning(
            `[업적기술] - 과정 및 노력을 입력해주세요 (${i + 1}행)`,
          );
          scrollToSelector(`[data-task-index="${i}"]`);
          return;
        }
      }

      // 자기 등급(셀렉트) 검증 - 수행업적
      const unevaluatedPerformance = performanceData.filter(
        (item) => !item.self || String(item.self).trim() === "",
      );
      if (unevaluatedPerformance.length > 0) {
        messageApi.warning(
          `업적 평가 항목 중 평가하지 않은 항목이 ${unevaluatedPerformance.length}개 있습니다.`,
        );
        scrollToSelector(`[data-perf-key="${unevaluatedPerformance[0].key}"]`);
        return;
      }

      // 자기 등급(셀렉트) 검증 - 역량
      const unevaluatedCompetency = competencyData.filter(
        (item) => !item.self || String(item.self).trim() === "",
      );
      if (unevaluatedCompetency.length > 0) {
        messageApi.warning(
          `역량 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCompetency.length}개 있습니다.`,
        );
        scrollToSelector(`[data-comp-key="${unevaluatedCompetency[0].key}"]`);
        return;
      }
    }

    // 1차 평가자 전용 검증: 능력개발/업무개선 의견(textarea) 및 1차 등급(select) 확인
    if (role === "first") {
      // 능력개발 및 업무 개선 사항 - 1차 의견 검증
      for (let i = 0; i < tableData.length; i++) {
        const row = tableData[i];
        if (!row.opinion || !String(row.opinion).trim()) {
          messageApi.warning(
            `${row.category || "개선활동"} - 1차 평가자 의견을 입력해주세요`,
          );
          scrollToSelector(`[data-improve-key="${row.key}"]`);
          return;
        }
      }

      // 1차 등급(셀렉트) 검증 - 수행업적
      const unevaluatedPerformance = performanceData.filter(
        (item) => !item.first || String(item.first).trim() === "",
      );
      if (unevaluatedPerformance.length > 0) {
        messageApi.warning(
          `업적 평가 항목 중 평가하지 않은 항목이 ${unevaluatedPerformance.length}개 있습니다.`,
        );
        scrollToSelector(`[data-perf-key="${unevaluatedPerformance[0].key}"]`);
        return;
      }

      // 1차 등급(셀렉트) 검증 - 역량
      const unevaluatedCompetency = competencyData.filter(
        (item) => !item.first || String(item.first).trim() === "",
      );
      if (unevaluatedCompetency.length > 0) {
        messageApi.warning(
          `역량 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCompetency.length}개 있습니다.`,
        );
        scrollToSelector(`[data-comp-key="${unevaluatedCompetency[0].key}"]`);
        return;
      }
    }

    // 2차 평가자 전용 검증: 능력개발/업무개선 의견(textarea) 및 2차 등급(select) 확인
    if (role === "second") {
      // 능력개발 및 업무 개선 사항 - 2차 의견 검증
      // 02/05 - 2차 평가자 의견은 필수검증에서 제외
      /* for (let i = 0; i < tableData.length; i++) {
        const row = tableData[i];
        if (!row.opinion2 || !String(row.opinion2).trim()) {
          return messageApi.warning(
            `${row.category || "개선활동"} - 2차 평가자 의견을 입력해주세요`,
          );
        }
      } */

      // 2차 등급(셀렉트) 검증 - 수행업적
      const unevaluatedPerformance = performanceData.filter(
        (item) => !item.second || String(item.second).trim() === "",
      );
      if (unevaluatedPerformance.length > 0) {
        messageApi.warning(
          `업적 평가 항목 중 평가하지 않은 항목이 ${unevaluatedPerformance.length}개 있습니다.`,
        );
        scrollToSelector(`[data-perf-key="${unevaluatedPerformance[0].key}"]`);
        return;
      }

      // 2차 등급(셀렉트) 검증 - 역량
      const unevaluatedCompetency = competencyData.filter(
        (item) => !item.second || String(item.second).trim() === "",
      );
      if (unevaluatedCompetency.length > 0) {
        messageApi.warning(
          `역량 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCompetency.length}개 있습니다.`,
        );
        scrollToSelector(`[data-comp-key="${unevaluatedCompetency[0].key}"]`);
        return;
      }
    }

    const performanceItems = data.map((row, idx) => ({
      hrMapId,
      hrEvlItmId:
        row.hrEvlItmId || (isJuniorRef.current ? "02010201" : "01010201"),
      writeSeq: String(idx + 1).padStart(2, "0"),
      taskNm: row.taskName,
      goalCntn: row.objective,
      resultCntn: row.performance,
      processCntn: row.effort,
      weightRt: row.weight ? Number(row.weight) : null,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
    }));

    const improvementItems = tableData.map((row: any, idx: number) => ({
      hrMapId,
      hrEvlItmId:
        row.hrEvlItmId || (isJuniorRef.current ? "02010201" : "01010201"),
      writeSeq: String(idx + 1).padStart(2, "0"),
      goalCntn: row.content,
      resultCntn: row.outcome,
      eval1Rmk: row.opinion,
      eval2Rmk: row.opinion2,
      lstChgEmpNo: loginEmpNo,
      lstChgGuid: crypto.randomUUID(),
      jobGrCd: selectedJobGrCd,
    }));

    // Web 버전과 동일하게 TOTAL 행 제외하고 평가 점수 데이터 가공
    const performanceEvalItems = performanceData
      .filter((item) => item.hrEvlItmId !== "TOTAL")
      .map((item) => ({
        hrMapId,
        hrEvlItmId: item.hrEvlItmId,
        selfGrade: item.self || "",
        eval1Grade: item.first || "",
        eval2Grade: item.second || "",
      }));
    const competencyEvalItems = competencyData
      .filter((item) => item.hrEvlItmId !== "TOTAL")
      .map((item) => ({
        hrMapId,
        hrEvlItmId: item.hrEvlItmId,
        selfGrade: item.self || "",
        eval1Grade: item.first || "",
        eval2Grade: item.second || "",
      }));

    const submitStatus = {
      slfSubYn: rawSearchData.slfSubYn || "N",
      eval1SubYn: rawSearchData.eval1SubYn || "N",
      eval2SubYn: rawSearchData.eval2SubYn || "N",
      ...(role === "self" && { slfSubYn: "Y" }),
      ...(role === "first" && { eval1SubYn: "Y" }),
      ...(role === "second" && { eval2SubYn: "Y" }),
    };

    const payloadPrefix = isJuniorRef.current ? "02" : "01";
    const targetEmpNo = rawSearchData?.empNo;

    const payload = {
      ...rawSearchData,
      ...submitStatus,
      hrMapId,
      role,
      hrEvlItmId: props.hrEvlItmId,
      empNo: loginEmpNo,
      jobGrCd: selectedJobGrCd,
      performanceItems,
      performanceData,
      improvementItems,
      performanceEvalItems,
      competencyEvalItems,
      competencyData,
      tableData,
      slfEvlTotScr: grandTotal.self,
      eval1TotScr: grandTotal.first,
      eval2TotScr: grandTotal.second,
      fnlScr: grandTotal.avg,
      evlYear,
      prefix: payloadPrefix,
      targetEmpNo,
    };

    commonProxy.callAsyncSvc(payload, {
      txCd: "TREVLS0001",
      success: (res: any) => {
        messageApi.success("제출되었습니다.");
        setTimeout(() => {
          common.openPage({ menuId: "MEVL2025002" });
        }, 800);
      },
      error: (err: any) => {
        messageApi.error("제출 중 오류가 발생했습니다.");
      },
    });
  };

  const performanceTotal = useMemo(
    () => calcTotal(performanceData),
    [performanceData],
  );

  const competencyTotal = useMemo(
    () => calcTotal(competencyData),
    [competencyData],
  );

  const grandTotal = useMemo(() => {
    const selfSum =
      Number(performanceTotal.self) + Number(competencyTotal.self);
    const firstSum =
      Number(performanceTotal.first) + Number(competencyTotal.first);
    const secondSum =
      Number(performanceTotal.second) + Number(competencyTotal.second);
    const avgTotal = secondSum; // 최종점수는 2차 평가 점수로 결정

    return {
      weight: performanceTotal.weight + competencyTotal.weight,
      self: selfSum.toFixed(2),
      first: firstSum.toFixed(2),
      second: secondSum.toFixed(2),
      avg: avgTotal.toFixed(2),
    };
  }, [performanceTotal, competencyTotal]);

  // 평가보기 탭 가시성 및 현재 보기 설정 (self/first/second)
  const visibleEvalTabs = useMemo(() => {
    if (role === "self") return [] as string[];
    if (role === "first") return ["self", "first"];
    return ["self", "first", "second"];
  }, [role]);

  const [evalView, setEvalView] = useState<"self" | "first" | "second">(() =>
    role === "self" ? "self" : role === "first" ? "first" : "second",
  );

  useEffect(() => {
    if (role === "self") setEvalView("self");
    else setEvalView(role === "first" ? "first" : "second");
  }, [role]);

  // 현재 보기(evalView)에 따라 업적평가 테이블의 컬럼 생성
  const getPerformanceColumns = (view: string) => {
    const cols: any[] = [
      {
        title: "항목",
        dataIndex: "item",
        key: "item",
        align: "center" as const,
        render: (value: string, row: any) => (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              style={{
                textAlign: "left",
                flex: 1,
                cursor: row.description ? "pointer" : "default",
                color: row.description ? "#1890ff" : "inherit",
                textDecoration: "none",
              }}
              onClick={() => {
                if (row.description) {
                  setSelectedItemName(value);
                  setSelectedItemDesc(row.description);
                  setDescModalVisible(true);
                }
              }}
            >
              {value}
            </span>
            <span style={{ marginLeft: 6, color: "#666", textAlign: "right" }}>
              {row?.weight ? `(${row.weight}점)` : ""}
            </span>
          </div>
        ),
      },
    ];

    // 본인 (always present)
    cols.push({
      title: "본인",
      dataIndex: "self",
      key: "self",
      align: "center" as const,
      width: view === "second" ? 70 : 100,
      render: (_: any, row: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Select
            value={row.self || undefined}
            onChange={(v) =>
              setPerformanceData((prev) =>
                prev.map((p) => (p.key === row.key ? { ...p, self: v } : p)),
              )
            }
            style={{ width: view === "second" ? 50 : 80, textAlign: "center" }}
            disabled={role !== "self" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "self" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
          >
            {gradeOptions.map((g) => (
              <Select.Option key={g} value={g}>
                {g}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    });

    // 1차
    if (view === "first" || view === "second") {
      cols.push({
        title: "1차",
        dataIndex: "first",
        key: "first",
        align: "center" as const,
        width: view === "second" ? 50 : 80,
        render: (_: any, row: any) => {
          // '1차' 보기인 경우 Select를 표시하여 1차 평가자가 편집 가능하도록 함
          if (view === "first" || view === "second") {
            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Select
                  value={row.first || undefined}
                  onChange={(v) =>
                    setPerformanceData((prev) =>
                      prev.map((p) =>
                        p.key === row.key ? { ...p, first: v } : p,
                      ),
                    )
                  }
                  style={{
                    width: view === "second" ? 50 : 80,
                    textAlign: "center",
                  }}
                  disabled={
                    role !== "first" || evlEnableYn === "N" || isSubmitted
                  }
                  suffixIcon={
                    role !== "first" || evlEnableYn === "N" || isSubmitted
                      ? null
                      : undefined
                  }
                >
                  {gradeOptions.map((g) => (
                    <Select.Option key={g} value={g}>
                      {g}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            );
          }
          return row.first || "";
        },
      });
    }

    // 2차
    if (view === "second") {
      cols.push({
        title: "2차",
        dataIndex: "second",
        key: "second",
        align: "center" as const,
        width: 50,
        render: (_: any, row: any) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Select
              value={row.second || undefined}
              onChange={(v) =>
                setPerformanceData((prev) =>
                  prev.map((p) =>
                    p.key === row.key ? { ...p, second: v } : p,
                  ),
                )
              }
              style={{ width: 50, textAlign: "center" }}
              disabled={role !== "second" || evlEnableYn === "N" || isSubmitted}
              suffixIcon={
                role !== "second" || evlEnableYn === "N" || isSubmitted
                  ? null
                  : undefined
              }
            >
              {gradeOptions.map((g) => (
                <Select.Option key={g} value={g}>
                  {g}
                </Select.Option>
              ))}
            </Select>
          </div>
        ),
      });
    }

    return cols;
  };

  // 현재 보기(evalView)에 따라 역량평가 테이블의 컬럼 생성
  const getCompetencyColumns = (view: string) => {
    const cols: any[] = [
      {
        title: "항목",
        dataIndex: "item",
        key: "item",
        align: "center" as const,
        render: (value: string, row: any) => (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              style={{
                textAlign: "left",
                flex: 1,
                cursor: row.description ? "pointer" : "default",
                color: row.description ? "#1890ff" : "inherit",
                textDecoration: "none",
              }}
              onClick={() => {
                if (row.description) {
                  setSelectedItemName(value);
                  setSelectedItemDesc(row.description);
                  setDescModalVisible(true);
                }
              }}
            >
              {value}
            </span>
            <span style={{ marginLeft: 6, color: "#666", textAlign: "right" }}>
              {row?.weight ? `(${row.weight}점)` : ""}
            </span>
          </div>
        ),
      },
    ];

    cols.push({
      title: "본인",
      dataIndex: "self",
      key: "self",
      align: "center" as const,
      width: view === "second" ? 70 : 100,
      render: (_: any, row: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Select
            value={row.self || undefined}
            onChange={(v) =>
              setCompetencyData((prev) =>
                prev.map((p) => (p.key === row.key ? { ...p, self: v } : p)),
              )
            }
            style={{ width: view === "second" ? 50 : 80, textAlign: "center" }}
            disabled={role !== "self" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "self" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
          >
            {gradeOptions.map((g) => (
              <Select.Option key={g} value={g}>
                {g}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    });

    if (view === "first" || view === "second") {
      cols.push({
        title: "1차",
        dataIndex: "first",
        key: "first",
        align: "center" as const,
        width: view === "second" ? 50 : 80,
        render: (_: any, row: any) => {
          if (view === "first" || view === "second") {
            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Select
                  value={row.first || undefined}
                  onChange={(v) =>
                    setCompetencyData((prev) =>
                      prev.map((p) =>
                        p.key === row.key ? { ...p, first: v } : p,
                      ),
                    )
                  }
                  style={{
                    width: view === "second" ? 50 : 80,
                    textAlign: "center",
                  }}
                  disabled={
                    role !== "first" || evlEnableYn === "N" || isSubmitted
                  }
                  suffixIcon={
                    role !== "first" || evlEnableYn === "N" || isSubmitted
                      ? null
                      : undefined
                  }
                >
                  {gradeOptions.map((g) => (
                    <Select.Option key={g} value={g}>
                      {g}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            );
          }
          return row.first || "";
        },
      });
    }

    if (view === "second") {
      cols.push({
        title: "2차",
        dataIndex: "second",
        key: "second",
        align: "center" as const,
        width: 50,
        render: (_: any, row: any) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Select
              value={row.second || undefined}
              onChange={(v) =>
                setCompetencyData((prev) =>
                  prev.map((p) =>
                    p.key === row.key ? { ...p, second: v } : p,
                  ),
                )
              }
              style={{ width: 50, textAlign: "center" }}
              disabled={role !== "second" || evlEnableYn === "N" || isSubmitted}
              suffixIcon={
                role !== "second" || evlEnableYn === "N" || isSubmitted
                  ? null
                  : undefined
              }
            >
              {gradeOptions.map((g) => (
                <Select.Option key={g} value={g}>
                  {g}
                </Select.Option>
              ))}
            </Select>
          </div>
        ),
      });
    }

    return cols;
  };

  // 역할/보기(role/view)에 따라 평가 결과(요약) 테이블의 컬럼 생성
  const getResultColumns = (view: string) => {
    const cols: any[] = [
      {
        title: "구분",
        dataIndex: "category",
        key: "category",
        align: "center" as const,
        width: 100,
      },
      {
        title: "비중",
        dataIndex: "weight",
        key: "weight",
        align: "center" as const,
        width: 60,
      },
    ];

    // 자기평가(항상 표시)
    cols.push({
      title: "자기 평가",
      dataIndex: "self",
      key: "self",
      align: "center" as const,
      width: 100,
      render: (t: any) => <b>{t || "0.0"}</b>,
    });

    if (view === "first" || view === "second") {
      cols.push({
        title: "1차 평가",
        dataIndex: "first",
        key: "first",
        align: "center" as const,
        width: 100,
        render: (t: any) => <b>{t || "0.0"}</b>,
      });
    }

    if (view === "second") {
      cols.push({
        title: "2차 평가",
        dataIndex: "second",
        key: "second",
        align: "center" as const,
        width: 100,
        render: (t: any) => <b>{t || "0.0"}</b>,
      });
    }

    return cols;
  };

  // 컬럼 및 소계 행 정의 (웹 버전과 동일한 동작)
  const performanceColumns = (
    setData: React.Dispatch<React.SetStateAction<any[]>>,
  ) => [
    {
      title: "구분",
      dataIndex: "category",
      key: "category",
      render: (value: string, record: any, index: number) => {
        if (record.key === "perf-total") {
          return {
            children: `${record.category} ${record.item}`,
            props: { colSpan: 2 },
          };
        }
        if (index === 0) return { children: value, props: { rowSpan: 5 } };
        if (index > 0 && index < 5)
          return { children: value, props: { rowSpan: 0 } };
        return value;
      },
    },
    {
      title: "항목",
      dataIndex: "item",
      key: "item",
      render: (value: string, record: any) => {
        if (record.key === "perf-total")
          return { children: "", props: { colSpan: 0 } };
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span style={{ textAlign: "left", flex: 1 }}>{value}</span>
            <span style={{ marginLeft: 6, color: "#666", textAlign: "right" }}>
              {record?.weight ? `(${record.weight}점)` : ""}
            </span>
          </div>
        );
      },
    },
    {
      title: "본인",
      dataIndex: "self",
      key: "self",
      render: (_: any, record: any) => {
        if (record.key === "perf-total") {
          return <b>{record.self}</b>;
        }
        return (
          <Select
            style={{ width: 80 }}
            value={record.self || undefined}
            disabled={role !== "self" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "self" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "self", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "1차",
      dataIndex: "first",
      key: "first",
      render: (_: any, record: any) => {
        if (record.key === "perf-total") return <b>{record.first}</b>;
        return (
          <Select
            style={{ width: 80 }}
            value={record.first || undefined}
            disabled={role !== "first" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "first" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "first", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "2차",
      dataIndex: "second",
      key: "second",
      render: (_: any, record: any) => {
        if (record.key === "perf-total") return <b>{record.second}</b>;
        return (
          <Select
            style={{ width: 80 }}
            value={record.second || undefined}
            disabled={role !== "second" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "second" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "second", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
  ];

  const performanceTableData = [
    ...performanceData,
    {
      key: "perf-total",
      category: "소계",
      item: "",
      weight: performanceTotal.weight,
      self: performanceTotal.self,
      first: performanceTotal.first,
      second: performanceTotal.second,
      isTotal: true,
    },
  ];

  const competencyColumns = (
    setData: React.Dispatch<React.SetStateAction<any[]>>,
  ) => [
    {
      title: "구분",
      dataIndex: "category",
      key: "category",
      render: (value: string, record: any, index: number) => {
        if (record.key === "comp-total")
          return {
            children: `${record.category} ${record.item}`,
            props: { colSpan: 2 },
          };
        if (index === 0) return { children: value, props: { rowSpan: 4 } };
        if (index > 0 && index < 4)
          return { children: value, props: { rowSpan: 0 } };
        if (index === 4) return { children: value, props: { rowSpan: 3 } };
        if (index > 4 && index < 7)
          return { children: value, props: { rowSpan: 0 } };
        if (index === 7) return { children: value, props: { rowSpan: 4 } };
        if (index > 7 && index < 11)
          return { children: value, props: { rowSpan: 0 } };
        return value;
      },
    },
    {
      title: "항목",
      dataIndex: "item",
      key: "item",
      render: (value: string, record: any) => {
        if (record.key === "comp-total")
          return { children: "", props: { colSpan: 0 } };
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span style={{ textAlign: "left", flex: 1 }}>{value}</span>
            <span style={{ marginLeft: 6, color: "#666", textAlign: "right" }}>
              {record?.weight ? `(${record.weight}점)` : ""}
            </span>
          </div>
        );
      },
    },
    {
      title: "본인",
      dataIndex: "self",
      key: "self",
      render: (_: any, record: any) => {
        if (record.key === "comp-total") return <b>{record.self}</b>;
        return (
          <Select
            style={{ width: 80 }}
            value={record.self || undefined}
            disabled={role !== "self" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "self" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "self", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "1차",
      dataIndex: "first",
      key: "first",
      render: (_: any, record: any) => {
        if (record.key === "comp-total") return <b>{record.first}</b>;
        return (
          <Select
            style={{ width: 80 }}
            value={record.first || undefined}
            disabled={role !== "first" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "first" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "first", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "2차",
      dataIndex: "second",
      key: "second",
      render: (_: any, record: any) => {
        if (record.key === "comp-total") return <b>{record.second}</b>;
        return (
          <Select
            style={{ width: 80 }}
            value={record.second || undefined}
            disabled={role !== "second" || evlEnableYn === "N" || isSubmitted}
            suffixIcon={
              role !== "second" || evlEnableYn === "N" || isSubmitted
                ? null
                : undefined
            }
            onChange={(val) =>
              handleSelectChange(val, record.key, "second", setData)
            }
          >
            {gradeOptions.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
  ];

  const competencyTableData = [
    ...competencyData,
    {
      key: "comp-total",
      category: "소계",
      item: "",
      weight: competencyTotal.weight,
      self: competencyTotal.self,
      first: competencyTotal.first,
      second: competencyTotal.second,
      isTotal: true,
    },
  ];
// 년도 가져오기
const currentYear = new Date().getFullYear();
// ROLE별 제출여부 차단
const blockKeyMap: Record<string, string[]> = {
  self: ["slfSubYn", "eval1SubYn"],     // 자기,1차 평가제출
  first: ["eval1SubYn", "eval2SubYn"],  // 1차,2차 평가제출
  second: ["eval2SubYn"],               // 2차평가 제출 
};

// hidden용 체크로직
const checkYn = (item: any): boolean => {

    if (!item) return false;
    let isBlocked = false;
    let msg = "";

    if (role === "self") {
      // 자기평가 → 1차평가 제출 여부 체크
      isBlocked = item?.eval1SubYn === "Y";
      if (isBlocked) {
        msg = "1차 평가자가 이미 제출하여 자기평가 저장 및 제출이 불가능합니다.";
      }

    } else if (role === "first") {
      // 1차평가 → 2차평가 제출 여부 체크
      isBlocked = item?.eval2SubYn === "Y";
      if (isBlocked) {
        msg = "2차 평가자가 이미 제출하여 1차 평가 저장 및 제출이 불가능합니다.";
      }

    } else if (role === "second") {
      // 2차평가 → 점수조정 사유 존재 여부 체크
      isBlocked = item?.scrAdjRsn !== null && item?.scrAdjRsn !== "";
      if (isBlocked) {
        msg = "점수조정 사유가 존재하여 2차 평가 저장 및 제출이 불가능합니다.";
      }
    }

    if (isBlocked) {
      Modal.confirm({
        title: "저장 및 제출이 불가능합니다.",
        content: msg,
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } },
        onOk: () => {
          setForceDisabled(true);
        }
      });
      return true;  // 차단
    }
    return false; // 통과
 };
// 저장 및 제출 체크
const checkSubmitYn = (): Promise<{ isBlocked: boolean, res: any }> => {

  return new Promise((resolve, reject) => {
  const requestData = { hrMapId, evlYear};

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",
      success: (json: any) => {

        const res = json.data ? json.data : json;
        if (!res?.performanceItems) {
          resolve({ isBlocked: false, res: null });
          return;
        }
       
        let isBlocked = false;
        let msg = "";
        const blockKeys = blockKeyMap[role] || [];
        if (role === "second") {
          // 2차평가자일땐 점수조정 사유 존재 여부 체크 추가
          isBlocked = res?.performanceItems[0]?.scrAdjRsn !== null;
        } else {
          isBlocked = res.performanceItems.some((item: any) => blockKeys.some((key) => item[key] === "Y")
          );
        }
        if (isBlocked) {
         
          if (role === "self") {
          // 자기평가 저장 시
          if (res?.performanceItems[0]?.eval1SubYn === "Y") {
            msg = "1차 평가자가 이미 제출하여 자기평가 저장 및 제출이 불가능합니다.";
          } else if (res?.performanceItems[0]?.slfSubYn === "Y") {
            msg = "자기평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          } else if (role === "first") {
          // 1차 평가 저장 시
          if (res?.performanceItems[0]?.eval2SubYn === "Y") {
            msg = "2차 평가자가 이미 제출하여 1차 평가 저장 및 제출이 불가능합니다.";
          } else if (res?.performanceItems[0]?.eval1SubYn === "Y") {
            msg = "1차 평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          } else if (role === "second") {
          // 2차 평가 저장 시
          if (res?.performanceItems[0]?.scrAdjRsn !== null && res?.performanceItems[0]?.scrAdjRsn !== "") {
            msg = "점수조정 사유가 존재하여 2차 평가 저장 및 제출이 불가능합니다."; 
          } else if (res?.performanceItems[0]?.eval2SubYn === "Y") {
            msg = "2차 평가가 이미 제출 완료되어 저장 및 제출이 불가능합니다.";
          }
          }
            Modal.confirm({
              title: "저장 및 제출이 불가능합니다.",
              content: msg,
              okText: "확인",
              cancelButtonProps: { style: { display: 'none' } }, 
              onOk: () => {
                setForceDisabled(true);
                resolve({ isBlocked: true, res });
              }
              });
              return;
            }
            resolve({ isBlocked: false, res });
      },
      error: (err: any) => {
        message.error("제출여부 조회중 에러가 발생하였습니다.");
        reject(err);
      }
    });
  });
};
// 제출취소용 저장 직전 체크로직(상위평가자 제출여부만 체크)
const checkSubmitYn2 = (): Promise<{ isBlocked: boolean, res: any }> => {
  return new Promise((resolve, reject) => {
  const requestData = { hrMapId, evlYear };
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0002",

      success: (json: any) => {
        const res = json.data ? json.data : json;
        const item = res?.performanceItems?.[0];
         setcheckData(res);   
        if (!res?.performanceItems) {
          resolve({ isBlocked: false, res: null });
          return;
        }
        let isBlocked = false;

        if (role === "self") {
          // 1차 평가자가 제출했는지 체크
          isBlocked = res.performanceItems.some((item: any) => item.eval1SubYn === "Y");
        }

        if (role === "first") {
          // 2차 평가자가 제출했는지 체크
          isBlocked = res.performanceItems.some((item: any) => item.eval2SubYn === "Y");
        }

        if (role === "second") {
          // 점수조정 사유 존재 여부 체크
          isBlocked = res.performanceItems.some((item: any) => item.scrAdjRsn !== null);
        }

        if (isBlocked) {
          Modal.confirm({
          title: "제출취소가 불가능합니다.",
          content: "상위평가자가 이미 제출 또는 점수조정을 완료했습니다.\n상위평가자에게 문의하세요.",
          okText: "확인",
          cancelButtonProps: { style: { display: 'none' } }, 
          onOk: () => {
            resolve({ isBlocked: true, res }); 
          }
          });
          return
        }
        resolve({ isBlocked: false, res });
      },
      error: (err: any) => {
        message.error("제출여부 조회중 에러가 발생하였습니다.");
        reject(err);
      }
    });
  });
};
 /**
   * 기능 : checkdate
   * 설명 : 변경된 제출기한 체크
   * param :
   * return : None
   **/
  const checkdate = (res: any) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    let targetDate: string | null = null;
    if (!res || !res.performanceItems[0] || res.performanceItems.length === 0) {
      return false;
    }
    const item = res.performanceItems[0];

    if (role === "self") {
      if (item.slfEvlModEdDt !== null && item.slfEvlModEdDt !== undefined && item.slfEvlModEdDt !== "") {
       targetDate = item.slfEvlModEdDt;
      }else{
        targetDate = item.slfEvlEdDt;
      }
    } else if (role === "first") {
      if (item.evl1EvlModEdDt !== null && item.evl1EvlModEdDt !== undefined && item.evl1EvlModEdDt !== "") {
        targetDate = item.evl1EvlModEdDt;
      }else{
         targetDate = item.eval1EdDt;
      }
    } else if (role === "second") {
      if (item.evl2EvlModEdDt !== null && item.evl2EvlModEdDt !== undefined && item.evl2EvlModEdDt !== "") {
        targetDate = item.evl2EvlModEdDt;
      }else{
         targetDate = item.eval2EdDt;
      }
    }

    if (!targetDate) return true;

    const year = Number(targetDate.substring(0,4));
    const month = Number(targetDate.substring(4,6)) - 1; // JS month는 0~11
    const day = Number(targetDate.substring(6,8));
    const checkDate = new Date(year, month, day);

    if (checkDate < today) {
      return false;
    }
    return true;
  }
    // 날짜 체크로직
    const isPast = (dateStr: string | null) => {
      if (!dateStr) return false; // 날짜가 없으면 체크 안 함
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 시간 제외하고 날짜만 비교
      return new Date(dateStr) < today;
    };
    // 버튼 노출 로직
    const checkHidden = async () => {
      // 1. 데이터 부재 시 기본값 처리
      const item = rawSearchData;
      if (!item) return true; 
      // 2. 공통 수정 기간 만료 체크 (ModEdDt)
      const isModPast = 
        isPast(item.slfEvlModEdDt) || 
        isPast(item.eval1ModEdDt) || 
        isPast(item.eval2ModEdDt);
      // 3. 역할(Role)별 평가 기간 만료 체크 (EdDt)
      let isPeriodPast = false;
      if (role === "self") isPeriodPast = isPast(item.slfEvlEdDt);
      else if (role === "first") isPeriodPast = isPast(item.eval1EdDt);
      else if (role === "second") isPeriodPast = isPast(item.eval2EdDt);
      // 수정기간 체크 및 기간체크 
        if (isModPast || isPeriodPast) {
          return true;
        }
      // 4. 이미 제출했는지 여부(상위평가자만 체크)
      const isBlocked =  checkYn(item);
      // 5. evlEditableYn 값이 N인경우 
      if(evlEditableYn === "N"){
        setForceDisabled(true); 
      }
      
      return isBlocked;
  
    };
  // mobile layout
  if (loading)
    return (
      <>
        {contextHolder}
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999, // 모달(z-index: 1000)보다 낮게 설정하여 background에만 표시
            background: "rgba(255,255,255,0.6)",
          }}
        >
          <Spin size="large" />
        </div>
      </>
    );
    const handleCancelSubmit = async () => {
      const { isBlocked, res } = await checkSubmitYn2();
        // 제출취소 체크
      if (isBlocked) {
          common.openPage({ menuId: 'MEVL2025002' });
          return;
        }
        // 제출기한 체크
      if(!checkdate(res)){
          alert("평가 기간이 만료되었습니다.");
          common.openPage({ menuId: 'MEVL2025002' });
          return;
      }
    
      // 제출취소시 룰별 업데이트 키
      let submitKey = "";
      if (role === "self") {
        submitKey = "slfSubYn";
      } else if (role === "first") {
        submitKey = "eval1SubYn";
      } else if (role === "second") {
        submitKey = "eval2SubYn";
      }
      
      const requestData = {
      hrMapId,
      evlYear,
      [submitKey]: "N"
    };
    
        commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0002",
        success: () => {
          message.success("제출 취소가 완료되었습니다.");
          common.openPage({ menuId: 'MEVL2025002' });
        },
        error: (err: any) => {
          console.error("제출 취소 중 에러 발생:", err);
          message.error("제출 취소 중 에러가 발생하였습니다.");
        }
      });
    }

  return (
    <>
      {contextHolder}
      <div
        style={{
          padding: isMobile ? 12 : 20,
          marginTop: 20,
          backgroundColor: "#f5f5f5",
          width: screenWidth - 20,
          paddingBottom: "60px",
        }}
      >
        <Title level={4} style={{ textAlign: "center", marginBottom: 12 }}>
          {evlYear}년도 인사평가표
        </Title>

        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <Text type="secondary">{isJunior ? "선임 이하" : "책임 이상"}</Text>
        </div>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>본인 정보</span>
          }
          style={{ marginBottom: 12, borderRadius: 12 }}
          bodyStyle={{ padding: 12 }}
        >
          {userInfoTableData.map((row) => (
            <div
              key={row.key}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  {row.label1}
                </div>
                <Input
                  value={row.value1}
                  disabled
                  style={{
                    width: "100%",
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  {row.label2}
                </div>
                <Input
                  value={row.value2}
                  disabled
                  style={{
                    width: "100%",
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  {row.label3}
                </div>
                {row.label3 === "직군" ? (
                  <div ref={jobGroupRef}>
                    <Select
                      value={row.value3 || undefined}
                      onChange={(v) =>
                        handleUserInfoChange(v, row.key, "value3")
                      }
                      placeholder="선택"
                      style={{ width: "100%", textAlign: "center" }}
                      disabled={
                        !(role === "self" && evlEnableYn !== "N") || isSubmitted
                      }
                    >
                      <Select.Option value="전산전문직">
                        전산전문직
                      </Select.Option>
                      <Select.Option value="비즈니스컨설턴트">
                        비즈니스컨설턴트
                      </Select.Option>
                      <Select.Option value="일반직">일반직</Select.Option>
                    </Select>
                  </div>
                ) : (
                  <Input
                    value={row.value3}
                    disabled
                    style={{
                      width: "100%",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </Card>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>평가자 정보</span>
          }
          style={{ marginBottom: 12, borderRadius: 12 }}
          bodyStyle={{ padding: 12 }}
        >
          {evaluatorTableData.map((r) => (
            <div key={r.key} style={{ marginBottom: 12 }}>
              <Divider
                style={{
                  margin: "3px 0",
                  fontSize: 12,
                  borderColor: "#D0E8FF",
                }}
              >
                {r.round} 평가자
              </Divider>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>이름</div>
                  <Input
                    value={r.value1}
                    disabled
                    style={{
                      width: "100%",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>직급</div>
                  <Input
                    value={r.value2}
                    disabled
                    style={{
                      width: "100%",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>직위</div>
                  <Input
                    value={r.value3}
                    disabled
                    style={{
                      width: "100%",
                      fontSize: 12,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </Card>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              능력개발 및 업무 개선 사항
            </span>
          }
          style={{ marginBottom: 12, borderRadius: 12 }}
          bodyStyle={{ padding: 10 }}
          ref={improvementSectionRef}
        >
          {role === "self" ? (
            <>
              {tableData.map((row) => (
                <div
                  key={row.key}
                  data-improve-key={row.key}
                  style={{ marginBottom: 12 }}
                >
                  <Divider
                    style={{
                      margin: "3px 0",
                      fontSize: 12,
                      borderColor: "#D0E8FF",
                    }}
                  >
                    {row.category}
                  </Divider>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      paddingBottom: 10,
                    }}
                  >
                    <div>
                      <TextArea
                        rows={3}
                        maxLength={500}
                        showCount
                        value={getDisplayValue(
                          "table",
                          row.key,
                          "content",
                          row.content,
                        )}
                        onChange={(e) =>
                          handleSimpleChange(
                            e.target.value,
                            row.key,
                            "content",
                            setTableData,
                            "table",
                          )
                        }
                        disabled={!canEdit.self}
                        placeholder="내용을 입력하십시오."
                      />
                    </div>
                    <div>
                      <TextArea
                        rows={3}
                        maxLength={500}
                        showCount
                        value={getDisplayValue(
                          "table",
                          row.key,
                          "outcome",
                          row.outcome,
                        )}
                        onChange={(e) =>
                          handleSimpleChange(
                            e.target.value,
                            row.key,
                            "outcome",
                            setTableData,
                            "table",
                          )
                        }
                        disabled={!canEdit.self}
                        placeholder="성과를 입력하십시오."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k)}>
              {visibleTabs.includes("self") && (
                <Tabs.TabPane tab="자기평가" key="self">
                  {tableData.map((row) => (
                    <div
                      key={row.key}
                      data-improve-key={row.key}
                      style={{ marginBottom: 12 }}
                    >
                      <Divider
                        style={{
                          margin: "3px 0",
                          fontSize: 12,
                          borderColor: "#D0E8FF",
                        }}
                      >
                        {row.category}
                      </Divider>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                          paddingBottom: 10,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              marginBottom: 6,
                              color: "#666",
                              fontWeight: 400,
                            }}
                          >
                            업무내용
                          </div>
                          <TextArea
                            rows={3}
                            maxLength={500}
                            showCount
                            value={getDisplayValue(
                              "table",
                              row.key,
                              "content",
                              row.content,
                            )}
                            onChange={(e) =>
                              handleSimpleChange(
                                e.target.value,
                                row.key,
                                "content",
                                setTableData,
                                "table",
                              )
                            }
                            disabled={!canEdit.self}
                            placeholder="내용을 입력하십시오."
                          />
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              marginBottom: 6,
                              color: "#666",
                              fontWeight: 400,
                            }}
                          >
                            성과
                          </div>
                          <TextArea
                            rows={3}
                            maxLength={500}
                            showCount
                            value={getDisplayValue(
                              "table",
                              row.key,
                              "outcome",
                              row.outcome,
                            )}
                            onChange={(e) =>
                              handleSimpleChange(
                                e.target.value,
                                row.key,
                                "outcome",
                                setTableData,
                                "table",
                              )
                            }
                            disabled={!canEdit.self}
                            placeholder="성과를 입력하십시오."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Tabs.TabPane>
              )}

              {visibleTabs.includes("first") && (
                <Tabs.TabPane tab="1차평가" key="first">
                  {tableData.map((row) => (
                    <div
                      key={row.key}
                      data-improve-key={row.key}
                      style={{ marginBottom: 12 }}
                    >
                      {/* <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {row.category}
                      </div> */}
                      <Divider
                        style={{
                          fontSize: 12,
                          margin: 2,
                          borderColor: "#D0E8FF",
                        }}
                      >
                        {row.category}
                      </Divider>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            marginBottom: 6,
                            color: "#666",
                            fontWeight: 400,
                          }}
                        >
                          1차 평가자 의견
                        </div>
                        <TextArea
                          rows={3}
                          placeholder="의견을 입력하십시오."
                          maxLength={500}
                          showCount
                          value={getDisplayValue(
                            "table",
                            row.key,
                            "opinion",
                            row.opinion,
                          )}
                          onChange={(e) =>
                            handleSimpleChange(
                              e.target.value,
                              row.key,
                              "opinion",
                              setTableData,
                              "table",
                            )
                          }
                          disabled={!canEdit.first}
                        />
                      </div>
                    </div>
                  ))}
                </Tabs.TabPane>
              )}

              {visibleTabs.includes("second") && (
                <Tabs.TabPane tab="2차평가" key="second">
                  {tableData.map((row) => (
                    <div
                      key={row.key}
                      data-improve-key={row.key}
                      style={{ marginBottom: 12 }}
                    >
                      {/* <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {row.category}
                      </div> */}
                      <Divider
                        style={{
                          fontSize: 12,
                          margin: 2,
                          borderColor: "#D0E8FF",
                        }}
                      >
                        {row.category}
                      </Divider>
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            marginBottom: 6,
                            color: "#666",
                            fontWeight: 400,
                          }}
                        >
                          2차 평가자 의견
                        </div>
                        <TextArea
                          rows={3}
                          placeholder={
                            canEdit.second ? "의견을 입력하십시오." : undefined
                          }
                          maxLength={500}
                          showCount
                          value={getDisplayValue(
                            "table",
                            row.key,
                            "opinion2",
                            row.opinion2,
                          )}
                          onChange={(e) =>
                            handleSimpleChange(
                              e.target.value,
                              row.key,
                              "opinion2",
                              setTableData,
                              "table",
                            )
                          }
                          disabled={!canEdit.second}
                        />
                      </div>
                    </div>
                  ))}
                </Tabs.TabPane>
              )}
            </Tabs>
          )}
        </Card>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>업적 기술</span>
          }
          style={{
            marginBottom: 12,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          bodyStyle={{ padding: "16px" }}
          ref={performanceSectionRef}
        >
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: 16, color: "#333" }}>
              업적(Performance)
            </Text>
            <div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddTask}
                disabled={role !== "self" || evlEnableYn === "N" || isSubmitted}
              >
                업무 추가
              </Button>
            </div>
          </div>

          <div
            style={{
              marginBottom: 12,
              padding: 12,
              backgroundColor: "#f5f5f5",
              borderRadius: 8,
              fontSize: 12,
              color: "#666",
            }}
          >
            업무 비중의 합계가 100%일때만 제출 가능합니다.
          </div>

          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {data.map((row, index) => (
              <Card
                key={row.key}
                data-task-index={index}
                size="small"
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "12px" }}
              >
                <div
                  style={{
                    marginBottom: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text strong style={{ fontSize: 14, color: "#333" }}>
                    업무 {index + 1}
                  </Text>

                  {data.length > 1 &&
                    role === "self" &&
                    evlEnableYn !== "N" && (
                      <Popconfirm
                        title="정말로 삭제하시겠습니까?"
                        onConfirm={() => deleteRow(row.key)}
                        okText="삭제"
                        cancelText="취소"
                      >
                        <Button
                          danger
                          size="small"
                          style={{ marginLeft: "6px" }}
                        >
                          삭제
                        </Button>
                      </Popconfirm>
                    )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 100px",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        /* backgroundColor: "#f5f5f5", */
                        padding: "4px 8px",
                        /* borderRadius: 6, */
                        marginBottom: 6,
                        /* color: "#666", */
                        display: "inline-block",
                      }}
                    >
                      업무명 :
                    </div>
                    <Input
                      placeholder="업무명"
                      value={row.taskName}
                      maxLength={100}
                      onChange={(e) =>
                        setData((prev) =>
                          prev.map((p) =>
                            p.key === row.key
                              ? { ...p, taskName: e.target.value }
                              : p,
                          ),
                        )
                      }
                      disabled={
                        role !== "self" || evlEnableYn === "N" || isSubmitted
                      }
                    />
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: 6,
                        marginBottom: 6,
                        color: "#666",
                        display: "inline-block",
                      }}
                    >
                      비중(%) :
                    </div>
                    <Input
                      placeholder="비중"
                      value={row.weight}
                      onChange={(e) => {
                        let raw = e.target.value.replace(/[^0-9]/g, "");
                        if (raw !== "") {
                          raw = String(Math.floor(Number(raw)));
                          if (Number(raw) > 100) raw = "100";
                        }
                        setData((prev) =>
                          prev.map((p) =>
                            p.key === row.key ? { ...p, weight: raw } : p,
                          ),
                        );
                      }}
                      onBlur={() => {
                        const current = data.find((d) => d.key === row.key);
                        const val = Number(current?.weight || 0);
                        if (val === 0) {
                          setData((prev) =>
                            prev.map((p) =>
                              p.key === row.key ? { ...p, weight: "1" } : p,
                            ),
                          );
                          messageApi.warning(
                            "비중은 1~100의 정수만 입력 가능합니다. 0은 1로 변경됩니다.",
                          );
                        }
                        const total = data.reduce(
                          (s, r) => s + Number(r.weight || 0),
                          0,
                        );
                        if (total > 100) {
                          messageApi.error(
                            `비중 합계가 100%를 초과했습니다. 현재 합계: ${total}%`,
                          );
                        }
                      }}
                      disabled={
                        role !== "self" || evlEnableYn === "N" || isSubmitted
                      }
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 8,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: 6,
                        marginBottom: 6,
                        color: "#666",
                        display: "inline-block",
                      }}
                    >
                      업무 목표 :
                    </div>
                    <TextArea
                      rows={3}
                      placeholder="업무 목표"
                      value={getDisplayValue(
                        "task",
                        row.key,
                        "objective",
                        row.objective,
                      )}
                      onChange={(e) =>
                        handleSimpleChange(
                          e.target.value,
                          row.key,
                          "objective",
                          setData,
                          "task",
                        )
                      }
                      disabled={
                        role !== "self" || evlEnableYn === "N" || isSubmitted
                      }
                      maxLength={500}
                      showCount
                    />
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: 6,
                        marginBottom: 6,
                        color: "#666",
                        display: "inline-block",
                      }}
                    >
                      업무 성과 :
                    </div>
                    <TextArea
                      rows={3}
                      placeholder="업무 성과"
                      value={getDisplayValue(
                        "task",
                        row.key,
                        "performance",
                        row.performance,
                      )}
                      onChange={(e) =>
                        handleSimpleChange(
                          e.target.value,
                          row.key,
                          "performance",
                          setData,
                          "task",
                        )
                      }
                      disabled={
                        role !== "self" || evlEnableYn === "N" || isSubmitted
                      }
                      maxLength={500}
                      showCount
                    />
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <div
                      style={{
                        fontSize: 12,
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: 6,
                        marginBottom: 6,
                        color: "#666",
                        display: "inline-block",
                      }}
                    >
                      성과에 대한 과정, 노력 :
                    </div>
                    <TextArea
                      rows={3}
                      placeholder="성과에 대한 과정, 노력"
                      value={getDisplayValue(
                        "task",
                        row.key,
                        "effort",
                        row.effort,
                      )}
                      onChange={(e) =>
                        handleSimpleChange(
                          e.target.value,
                          row.key,
                          "effort",
                          setData,
                          "task",
                        )
                      }
                      disabled={
                        role !== "self" || evlEnableYn === "N" || isSubmitted
                      }
                      maxLength={500}
                      showCount
                    />
                  </div>
                </div>
              </Card>
            ))}
          </Space>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div
              style={{
                marginTop: 12,
                padding: "12px",
                backgroundColor: weightTotal !== 100 ? "#fff2f0" : "#f6ffed",
                border: `1px solid ${weightTotal !== 100 ? "#ffccc7" : "#b7eb8f"}`,
                borderRadius: "8px",
                textAlign: "center",
                minWidth: 200,
              }}
            >
              <Text
                strong
                style={{ color: weightTotal !== 100 ? "#cf1322" : "#52c41a" }}
              >
                합계: {weightTotal}%{" "}
                {weightTotal !== 100 && "(100%가 되어야 제출 가능)"}
              </Text>
            </div>
          </div>
        </Card>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              업적 / 역량 평가
            </span>
          }
          style={{ marginBottom: 12, borderRadius: 12 }}
          bodyStyle={{ padding: 12 }}
          ref={performanceEvalRef}
        >
          {/* 탭: 자기평가/1차/2차 (role에 따라 가시성 제어) - 숨김 처리 */}
          {false && (
            <Tabs
              activeKey={evalView}
              onChange={(k) => setEvalView(k as "self" | "first" | "second")}
              size="small"
              style={{ marginBottom: 8 }}
            >
              {visibleEvalTabs.map((tabKey) => (
                <Tabs.TabPane
                  tab={
                    tabKey === "self"
                      ? "자기평가"
                      : tabKey === "first"
                        ? "1차평가"
                        : "2차평가"
                  }
                  key={tabKey}
                />
              ))}
            </Tabs>
          )}

          <Divider
            style={{ margin: "3px 0", fontSize: 12, borderColor: "#D0E8FF" }}
          >
            업적평가
          </Divider>

          <Table
            columns={getPerformanceColumns(evalView)}
            dataSource={performanceData}
            pagination={false}
            bordered
            size="small"
            rowKey={(r) => r.key}
            onRow={(record) =>
              ({
                "data-perf-key": record.key,
              }) as React.HTMLAttributes<HTMLElement>
            }
          />

          <Divider
            style={{
              margin: "6px 0 3px 0",
              fontSize: 12,
              borderColor: "#D0E8FF",
            }}
          >
            역량평가
          </Divider>

          <Table
            columns={getCompetencyColumns(evalView)}
            dataSource={competencyData}
            pagination={false}
            bordered
            size="small"
            rowKey={(r) => r.key}
            onRow={(record) =>
              ({
                "data-comp-key": record.key,
              }) as React.HTMLAttributes<HTMLElement>
            }
          />
        </Card>

        <Card
          title={
            <span style={{ fontSize: 16, fontWeight: 600 }}>평가 결과</span>
          }
          style={{ marginBottom: 12, borderRadius: 12 }}
          bodyStyle={{ padding: 12 }}
        >
          <Table
            columns={getResultColumns(evalView)}
            dataSource={[
              {
                key: "1",
                category: "업적평가",
                weight: performanceTotal.weight,
                self: performanceTotal.self,
                first: performanceTotal.first,
                second: performanceTotal.second,
              },
              {
                key: "2",
                category: "역량평가",
                weight: competencyTotal.weight,
                self: competencyTotal.self,
                first: competencyTotal.first,
                second: competencyTotal.second,
              },
              {
                key: "3",
                category: "총계",
                weight: grandTotal.weight,
                self: grandTotal.self,
                first: grandTotal.first,
                second: grandTotal.second,
              },
            ]}
            pagination={false}
            bordered
            size="small"
          />
        </Card>
             {isHidden || forceDisabled ? null : (
            <>
         {/* 하단 버튼 영역 */}
      <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: 32 }}>
        {/* 제출취소 버튼 노출 조건: 제출 완료했거나, 권한이 없거나, 평가 기간이 아닐 때 */}
       
        {isSubmitted || role === "none" || evlEnableYn === "N" ? (
          <Button
            type="primary"
            danger
            size="large"
            icon={isSubmitted ? <CheckCircleFilled /> : <SendOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "제출을 취소하시겠습니까?",
                okText: "확인",
                cancelText: "취소",
                onOk: () => handleCancelSubmit(),
              });
            }}
            style={{
              flex: 1,
              height: "48px",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            제출취소
          </Button>
        ) : (
          /* 아직 제출 전일 때 (저장/제출 버튼) */
          <>
            <Button
              size="large"
             icon={isSubmitted ? <CheckCircleFilled /> : <SendOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: "저장하시겠습니까?",
                  okText: "확인",
                  cancelText: "취소",
                  onOk: () => handleSave(),
                });
              }}
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              저장
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              disabled={weightTotal !== 100}
              onClick={() => {
                Modal.confirm({
                  title: "제출하시겠습니까?",
                  okText: "확인",
                  cancelText: "취소",
                  onOk: () => handleSubmit(),
                });
              }}
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              제출
            </Button>
          </>
        )}      
      </div>
      </>
      )}
        </div>
      {/* 업적/역량평가 항목 상세설명 Modal */}
      <Modal
        title={`상세설명 - ${selectedItemName}`}
        open={descModalVisible}
        onCancel={() => setDescModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDescModalVisible(false)}>
            닫기
          </Button>,
        ]}
        width={isMobile ? "90vw" : 600}
      >
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {selectedItemDesc}
        </div>
      </Modal>
    </>
  );
}
